<template>
  <div class="bank-session stack tight">
    <UiPanel tone="econ">
      <div class="header">
        <div>
          <h3 class="title">{{ ICONS.places.bank }} Storage</h3>
          <div class="subtitle text-muted">Banking and containers.</div>
        </div>
        <div class="header-right">
          <UiChip
            v-if="carryState"
            :variant="carryState.heavyEncumbered ? 'danger' : carryState.encumbered ? 'warning' : 'muted'"
            class="stat-mono num"
          >
            {{ formatNumber(carryState.slotsUsed) }} / {{ formatNumber(carryState.carryCapacitySlots) }}
            <span v-if="carryState.heavyEncumbered">Heavy</span>
            <span v-else-if="carryState.encumbered">Encumbered</span>
          </UiChip>
          <UiChip v-if="loading" variant="muted">Loading</UiChip>
          <UiChip v-else-if="isBankStale" variant="warning">Stale</UiChip>
        </div>
      </div>

      <div class="controls">
        <UiTabs v-model="tab" :tabs="tabs" tone="econ" />
        <div v-if="tab === 'containers'" class="text-muted">Container contents sync automatically.</div>
      </div>
    </UiPanel>

    <UiCallout v-if="tab === 'bank' && !available" variant="warning">
      {{ blockedReason || "You need to be at a bank to access storage." }}
    </UiCallout>

    <template v-else-if="tab === 'bank'">
      <UiCallout v-if="error" variant="danger">{{ error }}</UiCallout>

      <div v-if="state" class="bank-grid">
        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Gold</template>
            <template #actions>
              <UiButton size="sm" variant="secondary" :disabled="loading" @click="depositAllGold">Deposit All</UiButton>
            </template>
          </UiSectionHeader>

          <div class="gold-kv">
            <div>
              <div class="kv-label">Wallet</div>
              <div class="kv-value stat-mono num">{{ formatNumber(state.character.gold) }} gp</div>
            </div>
            <div>
              <div class="kv-label">Bank</div>
              <div class="kv-value stat-mono num">{{ formatNumber(state.bank.gold) }} gp</div>
            </div>
          </div>

          <div class="row wrap" style="justify-content: flex-end;">
            <UiButton size="sm" variant="secondary" :disabled="loading || state.character.gold <= 0" @click="promptGold('deposit')">
              Deposit…
            </UiButton>
            <UiButton size="sm" variant="secondary" :disabled="loading || state.bank.gold <= 0" @click="promptGold('withdraw')">
              Withdraw…
            </UiButton>
          </div>
        </UiPanel>

        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Inventory → Bank</template>
            <template #actions>
              <label class="search">
                <span class="sr-only">Search inventory</span>
                <input v-model="invSearch" class="ui-input" placeholder="Search…" />
              </label>
              <UiButton size="sm" variant="secondary" :disabled="loading" @click="depositAllItems">Deposit All</UiButton>
            </template>
          </UiSectionHeader>

          <div v-if="filteredInventory.length === 0" class="text-muted">No items to deposit.</div>
          <div v-else class="list">
            <div v-for="entry in filteredInventory" :key="invKey(entry)" class="item-row">
              <div class="icon" aria-hidden="true">{{ itemIconForType(entry.item?.type) }}</div>
              <div style="min-width: 0;">
                <div class="item-name">{{ entry.item?.name || entry.itemId }}</div>
                <div class="text-muted stat-mono num">×{{ formatNumber(entry.quantity) }} • {{ markForEntry(entry) }}</div>
              </div>
              <UiButton size="sm" tone="econ" :disabled="loading" @click="depositEntry(entry)">Deposit…</UiButton>
            </div>
          </div>
        </UiPanel>

        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Bank → Inventory</template>
            <template #actions>
              <label class="search">
                <span class="sr-only">Search bank</span>
                <input v-model="bankSearch" class="ui-input" placeholder="Search…" />
              </label>
            </template>
          </UiSectionHeader>

          <div v-if="filteredBank.length === 0" class="text-muted">No items in bank.</div>
          <div v-else class="list">
            <div v-for="entry in filteredBank" :key="bankKey(entry)" class="item-row">
              <div class="icon" aria-hidden="true">{{ itemIconForType(entry.item?.type) }}</div>
              <div style="min-width: 0;">
                <div class="item-name">{{ entry.item?.name || entry.itemId }}</div>
                <div class="text-muted stat-mono num">×{{ formatNumber(entry.quantity) }} • {{ markForEntry(entry) }}</div>
              </div>
              <UiButton size="sm" variant="secondary" :disabled="loading" @click="withdrawEntry(entry)">Withdraw…</UiButton>
            </div>
          </div>
        </UiPanel>
      </div>
    </template>

    <template v-else>
      <UiCallout v-if="containers.error.value" variant="danger">{{ containers.error.value }}</UiCallout>
      <UiCallout v-else-if="containers.loading.value" variant="info">Loading containers…</UiCallout>

      <template v-else-if="containers.state.value">
        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Carry</template>
            <template #actions>
              <UiChip variant="muted" class="stat-mono num">
                {{ formatNumber(containers.state.value.carry.slotsUsed) }} /
                {{ formatNumber(containers.state.value.carry.carryCapacitySlots) }}
              </UiChip>
              <UiChip v-if="containers.state.value.carry.encumbered" variant="warning">Encumbered</UiChip>
            </template>
          </UiSectionHeader>

          <div class="carry-grid">
            <div class="kv">
              <div class="kv-label">Back Slot</div>
              <div class="kv-value">
                <template v-if="equippedContainerLabel">{{ equippedContainerLabel }}</template>
                <template v-else>—</template>
              </div>
            </div>
            <div class="kv">
              <div class="kv-label">Containers</div>
              <div class="kv-value stat-mono num">{{ formatNumber(containers.state.value.containers.length) }}</div>
            </div>
          </div>
        </UiPanel>

        <div class="containers-grid">
          <UiPanel tone="econ">
            <UiSectionHeader>
              <template #title>Your Containers</template>
              <template #actions>
                <UiChip variant="muted" class="stat-mono num">{{ formatNumber(containers.state.value.containers.length) }}</UiChip>
              </template>
            </UiSectionHeader>

            <UiCallout v-if="containers.state.value.containers.length === 0" variant="info">No open containers.</UiCallout>
            <div v-else class="list">
              <button
                v-for="container in containers.state.value.containers"
                :key="container.instanceId"
                type="button"
                class="container-row"
                :class="{ active: container.instanceId === activeInstanceId }"
                @click="activeInstanceId = container.instanceId"
              >
                <span class="icon" aria-hidden="true">{{ ICONS.itemsByType.container }}</span>
                <span class="container-row__main">
                  <span class="container-row__name">
                    {{ container.itemName || container.itemKey }}
                    <UiChip v-if="container.instanceId === equippedContainerInstanceId" variant="accent">Equipped</UiChip>
                  </span>
                  <span class="text-muted stat-mono num">{{ capacityLabel(container) }}</span>
                </span>
              </button>
            </div>

            <UiSectionHeader style="margin-top: 0.85rem;">
              <template #title>Empty Containers</template>
            </UiSectionHeader>

            <UiCallout v-if="emptyContainers.length === 0" variant="muted">No empty containers in inventory.</UiCallout>
            <div v-else class="list">
              <div v-for="entry in emptyContainers" :key="invKey(entry)" class="item-row">
                <div class="icon" aria-hidden="true">{{ itemIconForType(entry.item?.type) }}</div>
                <div style="min-width: 0;">
                  <div class="item-name">{{ entry.item?.name || entry.itemId }}</div>
                  <div class="text-muted stat-mono num">×{{ formatNumber(entry.quantity) }} • {{ markForEntry(entry) }}</div>
                </div>
                <UiButton size="sm" tone="econ" :disabled="containers.loading.value" @click="openEmpty(entry.itemId)">
                  Open
                </UiButton>
              </div>
            </div>
          </UiPanel>

          <UiPanel tone="econ">
            <UiSectionHeader>
              <template #title>Contents</template>
              <template #actions>
                <UiChip v-if="activeContainer" variant="muted" class="stat-mono num">{{ capacityLabel(activeContainer) }}</UiChip>
              </template>
            </UiSectionHeader>

            <UiCallout v-if="!activeContainer" variant="info">Select a container to manage.</UiCallout>

            <template v-else>
              <div class="row wrap" style="justify-content: flex-end; margin-bottom: 0.5rem;">
                <UiButton size="sm" variant="secondary" :disabled="containers.loading.value" @click="equipActive">
                  Equip
                </UiButton>
                <UiButton
                  size="sm"
                  variant="secondary"
                  :disabled="containers.loading.value || activeContainer.contents.length > 0"
                  @click="packAwayActive"
                >
                  Pack Away
                </UiButton>
              </div>

              <UiSectionHeader>
                <template #title>Stored</template>
                <template #actions>
                  <UiChip variant="muted" class="stat-mono num">{{ formatNumber(activeContainer.contents.length) }}</UiChip>
                </template>
              </UiSectionHeader>

              <div v-if="activeContainer.contents.length === 0" class="text-muted">No items stored.</div>
              <div v-else class="list">
                <button
                  v-for="item in activeContainer.contents"
                  :key="invKey(item)"
                  type="button"
                  class="container-item"
                  @click="unpackPrompt(item)"
                >
                  <span class="icon" aria-hidden="true">{{ itemIconForType(item.item?.type) }}</span>
                  <span class="container-item__main">
                    <span class="item-name">{{ item.item?.name || item.itemId }}</span>
                    <span class="text-muted stat-mono num">×{{ formatNumber(item.quantity) }} • {{ markForEntry(item) }}</span>
                  </span>
                  <span class="container-item__hint text-muted">Unpack</span>
                </button>
              </div>

              <UiSectionHeader style="margin-top: 0.85rem;">
                <template #title>Pack From Inventory</template>
                <template #actions>
                  <label class="search">
                    <span class="sr-only">Search inventory</span>
                    <input v-model="containerSearch" class="ui-input" placeholder="Search…" />
                  </label>
                </template>
              </UiSectionHeader>

              <div v-if="packableInventory.length === 0" class="text-muted">No packable items.</div>
              <div v-else class="list">
                <button
                  v-for="entry in packableInventory"
                  :key="invKey(entry)"
                  type="button"
                  class="container-item"
                  @click="packPrompt(entry)"
                >
                  <span class="icon" aria-hidden="true">{{ itemIconForType(entry.item?.type) }}</span>
                  <span class="container-item__main">
                    <span class="item-name">{{ entry.item?.name || entry.itemId }}</span>
                    <span class="text-muted stat-mono num">×{{ formatNumber(entry.quantity) }} • {{ markForEntry(entry) }}</span>
                  </span>
                  <span class="container-item__hint text-muted">Pack</span>
                </button>
              </div>
            </template>
          </UiPanel>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { BankState, ContainerState, ContainersResponse, InventoryEntry } from "@idle-rpg/shared";
import {
  apiBankDepositAll,
  apiBankGold,
  apiBankInstance,
  apiBankInstances,
  apiBankStackable,
  apiEquipItem,
  apiOpenContainer,
  apiPackAwayContainer,
  apiPackContainer,
  apiUnpackContainer
} from "../../../api/client";
import { useSessionSocket } from "../../../composables/useSessionSocket";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import { getItemMarkDisplay } from "../../../lib/itemMarks";
import { itemIconForType } from "../../../lib/itemIcons";
import { compareEntriesByStackedValue } from "../../../lib/inventorySort";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import { useQuantityPrompt } from "../../../composables/useQuantityPrompt";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import { useContainersState } from "../application/useContainersState";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiTabs from "../../../components/ui/UiTabs.vue";

const props = withDefaults(
  defineProps<{
    available: boolean;
    blockedReason?: string | null;
    initialTab?: "bank" | "containers";
  }>(),
  {
    blockedReason: null,
    initialTab: "bank"
  }
);

const session = useSessionStore();
const { addToast } = useToast();
const { promptQuantity } = useQuantityPrompt();
const { mode: itemMarkMode } = useItemMarkMode();
const sessionSocket = useSessionSocket();
const containers = useContainersState();
const BANK_STALE_MS = 2 * 60 * 1000;

const tab = ref<"bank" | "containers">(props.initialTab);
watch(
  () => props.initialTab,
  (next) => {
    if (next) tab.value = next;
  }
);

const tabs = computed(() => [
  { value: "bank", label: "Bank", icon: ICONS.places.bank },
  { value: "containers", label: "Containers", icon: ICONS.itemsByType.container }
]);

watch(
  () => tab.value,
  async (next) => {
    if (next !== "containers") return;
    await refreshContainers({ force: !containers.state.value });
  }
);

const state = ref<BankState | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const invSearch = ref("");
const bankSearch = ref("");
const activeInstanceId = ref<string | null>(null);
const containerSearch = ref("");
const lastUpdatedAt = ref<number | null>(null);
const carryState = computed(() => state.value?.character?.carry ?? null);
const isBankStale = computed(() => {
  if (!lastUpdatedAt.value) return false;
  return Date.now() - lastUpdatedAt.value > BANK_STALE_MS;
});
let bankWsUnsubscriber: (() => void) | null = null;

function startBankStream() {
  if (!props.available || tab.value !== "bank") return;
  sessionSocket.subscribe(["bank"]);
  bankWsUnsubscriber?.();
  bankWsUnsubscriber = sessionSocket.onEvent("bank", (msg) => {
    if (msg.event !== "bank.updated") return;
    if (!props.available || tab.value !== "bank") return;
    void refresh({ silent: true });
  });
}

function stopBankStream() {
  bankWsUnsubscriber?.();
  bankWsUnsubscriber = null;
  sessionSocket.unsubscribe(["bank"]);
}

function markForEntry(entry: InventoryEntry): string {
  return getItemMarkDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null);
}

function requestId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

async function refresh(options?: { silent?: boolean }) {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  const silent = options?.silent ?? false;
  if (!silent) {
    loading.value = true;
  }
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("bank");
    state.value = snapshot.data as BankState;
    session.character.gold = state.value.character.gold;
    session.character.inventory = state.value.character.inventory as InventoryEntry[];
    session.character.carry = state.value.character.carry as any;
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load bank storage. Please retry.";
  } finally {
    if (!silent) {
      loading.value = false;
    }
  }
}

async function refreshContainers(options?: { force?: boolean }) {
  await containers.refresh({ force: options?.force ?? true });
  if (!activeInstanceId.value && containers.state.value?.containers?.length) {
    activeInstanceId.value = containers.state.value.containers[0]?.instanceId ?? null;
  }
}

const equippedContainerInstanceId = computed(() => containers.state.value?.equipment?.back?.instanceIds?.[0] ?? null);
const equippedContainerLabel = computed(() => containers.state.value?.equipment?.back?.item?.name ?? null);
const activeContainer = computed<ContainerState | null>(
  () => containers.state.value?.containers?.find((c) => c.instanceId === activeInstanceId.value) ?? null
);

const emptyContainers = computed(() =>
  (containers.state.value?.inventory ?? [])
    .filter((e) => e.item?.type === "container" && !e.instanceIds && e.quantity > 0)
    .sort(compareEntriesByStackedValue)
);

const packableInventory = computed(() => {
  const list = containers.state.value?.inventory ?? [];
  const q = containerSearch.value.trim().toLowerCase();
  const isQuiver = activeContainer.value?.kind === "quiver";
  const filtered = list.filter((e) => {
    if (e.itemId === "item-gold") return false;
    if (e.item?.type === "container") return false;
    if (isQuiver) {
      const isAmmo = e.item?.tags?.includes("ammo") || e.item?.type === "ammo";
      if (!isAmmo) return false;
    }
    return e.quantity > 0;
  });
  const searched = q ? filtered.filter((e) => (e.item?.name || e.itemId).toLowerCase().includes(q)) : filtered;
  return searched.sort(compareEntriesByStackedValue);
});

function capacityLabel(container: ContainerState): string {
  if (container.kind === "quiver") {
    const cap = container.maxQuantity ?? container.capacitySlots ?? 0;
    return `${formatNumber(container.totalQuantity)} / ${formatNumber(cap)} ammo`;
  }
  const cap = container.capacitySlots ?? 0;
  return `${formatNumber(container.slotsUsed)} / ${formatNumber(cap)} slots`;
}

async function openEmpty(itemId: string) {
  if (!session.token || !session.character) return;
  containers.loading.value = true;
  const res = await apiOpenContainer(session.token, {
    characterId: session.character.id,
    emptyItemId: itemId,
    clientRequestId: requestId()
  });
  containers.loading.value = false;
  if (!res.ok || !res.data) {
    addToast("error" in res ? res.error?.message || "Failed to open container" : "Failed to open container", "warning");
    return;
  }
  containers.apply(res.data as ContainersResponse);
  activeInstanceId.value = (res.data as ContainersResponse).containers?.[0]?.instanceId ?? activeInstanceId.value;
}

async function packPrompt(entry: InventoryEntry) {
  if (!activeContainer.value) return;
  const max = Math.max(1, Math.floor(Number(entry.quantity || 1)));
  const qty = await promptQuantity({
    title: "Pack quantity",
    subtitle: `${entry.item?.name || entry.itemId} → ${activeContainer.value.itemName || activeContainer.value.itemKey}`,
    icon: ICONS.itemsByType.container,
    min: 1,
    max,
    initial: Math.min(1, max),
    confirmLabel: "Pack"
  });
  if (!qty) return;
  await packIntoContainer(activeContainer.value.instanceId, entry, qty);
}

async function unpackPrompt(entry: InventoryEntry) {
  if (!activeContainer.value) return;
  const max = Math.max(1, Math.floor(Number(entry.quantity || 1)));
  const qty = await promptQuantity({
    title: "Unpack quantity",
    subtitle: `${entry.item?.name || entry.itemId} → Inventory`,
    icon: ICONS.itemsByType.container,
    min: 1,
    max,
    initial: Math.min(1, max),
    confirmLabel: "Unpack"
  });
  if (!qty) return;
  await unpackFromContainer(activeContainer.value.instanceId, entry, qty);
}

async function packIntoContainer(containerInstanceId: string, entry: InventoryEntry, quantity: number) {
  if (!session.token || !session.character) return;
  containers.loading.value = true;
  const res = await apiPackContainer(session.token, {
    characterId: session.character.id,
    containerInstanceId,
    itemId: entry.itemId,
    quantity,
    enchantmentId: entry.enchantment?.id ?? null,
    clientRequestId: requestId()
  });
  containers.loading.value = false;
  if (!res.ok || !res.data) {
    addToast("error" in res ? res.error?.message || "Failed to pack item" : "Failed to pack item", "warning");
    return;
  }
  containers.apply(res.data as ContainersResponse);
}

async function unpackFromContainer(containerInstanceId: string, entry: InventoryEntry, quantity: number) {
  if (!session.token || !session.character) return;
  containers.loading.value = true;
  const res = await apiUnpackContainer(session.token, {
    characterId: session.character.id,
    containerInstanceId,
    itemId: entry.itemId,
    quantity,
    enchantmentId: entry.enchantment?.id ?? null,
    clientRequestId: requestId()
  });
  containers.loading.value = false;
  if (!res.ok || !res.data) {
    addToast("error" in res ? res.error?.message || "Failed to unpack item" : "Failed to unpack item", "warning");
    return;
  }
  containers.apply(res.data as ContainersResponse);
}

async function packAwayActive() {
  if (!activeContainer.value) return;
  if (!session.token || !session.character) return;
  containers.loading.value = true;
  const res = await apiPackAwayContainer(session.token, {
    characterId: session.character.id,
    containerInstanceId: activeContainer.value.instanceId,
    clientRequestId: requestId()
  });
  containers.loading.value = false;
  if (!res.ok || !res.data) {
    addToast("error" in res ? res.error?.message || "Failed to pack away container" : "Failed to pack away container", "warning");
    return;
  }
  containers.apply(res.data as ContainersResponse);
  activeInstanceId.value = null;
}

async function equipActive() {
  if (!activeContainer.value) return;
  if (!session.token || !session.character) return;
  const res = await apiEquipItem(session.token, session.character.id, {
    itemId: activeContainer.value.itemId,
    instanceId: activeContainer.value.instanceId,
    slot: "back",
    clientRequestId: requestId()
  });
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Failed to equip container" : "Failed to equip container", "warning");
    return;
  }
  await refreshContainers({ force: true });
}

async function bankGold(direction: "deposit" | "withdraw", amount: number) {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  loading.value = true;
  const res = await apiBankGold(session.token, {
    characterId: session.character.id,
    amount,
    direction,
    clientRequestId: requestId()
  });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Failed to move gold" : "Failed to move gold", "warning");
    return;
  }
  await refresh({ silent: true });
}

async function depositAllGold() {
  if (!state.value) return;
  if (state.value.character.gold <= 0) return;
  await bankGold("deposit", state.value.character.gold);
}

async function promptGold(direction: "deposit" | "withdraw") {
  if (!state.value) return;
  const max = direction === "deposit" ? state.value.character.gold : state.value.bank.gold;
  if (max <= 0) return;
  const amount = await promptQuantity({
    title: direction === "deposit" ? "Deposit gold" : "Withdraw gold",
    subtitle: direction === "deposit" ? "Move gold from wallet into bank storage." : "Move gold from bank storage into wallet.",
    icon: ICONS.itemsByType.currency,
    min: 1,
    max,
    initial: Math.min(max, 100),
    confirmLabel: direction === "deposit" ? "Deposit" : "Withdraw",
    helperText: `Max ${formatNumber(max)} gp`
  });
  if (!amount) return;
  await bankGold(direction, amount);
}

async function depositAllItems() {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  loading.value = true;
  const res = await apiBankDepositAll(session.token, { characterId: session.character.id, clientRequestId: requestId() });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Deposit all failed" : "Deposit all failed", "warning");
    return;
  }
  await refresh({ silent: true });
}

function invKey(entry: InventoryEntry) {
  return `${entry.itemId}-${entry.enchantment?.id || "base"}-${entry.instanceIds?.[0] || "stack"}`;
}

function bankKey(entry: InventoryEntry) {
  return invKey(entry);
}

const filteredInventory = computed(() => {
  const list = state.value?.character.inventory ?? [];
  const q = invSearch.value.trim().toLowerCase();
  const filtered = q ? list.filter((e) => (e.item?.name || e.itemId).toLowerCase().includes(q)) : [...list];
  return filtered.sort(compareEntriesByStackedValue);
});

const filteredBank = computed(() => {
  const list = state.value?.bank.inventory ?? [];
  const q = bankSearch.value.trim().toLowerCase();
  const filtered = q ? list.filter((e) => (e.item?.name || e.itemId).toLowerCase().includes(q)) : [...list];
  return filtered.sort(compareEntriesByStackedValue);
});

async function depositEntry(entry: InventoryEntry) {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  const isStackable = !entry.instanceIds || entry.instanceIds.length === 0;
  if (isStackable) {
    const max = Math.max(1, Math.floor(Number(entry.quantity || 1)));
    const qty = await promptQuantity({
      title: "Deposit quantity",
      subtitle: entry.item?.name || entry.itemId,
      icon: itemIconForType(entry.item?.type),
      min: 1,
      max,
      initial: max,
      confirmLabel: "Deposit"
    });
    if (!qty) return;
    loading.value = true;
    const res = await apiBankStackable(session.token, {
      characterId: session.character.id,
      itemId: entry.itemId,
      quantity: qty,
      direction: "deposit",
      clientRequestId: requestId()
    });
    loading.value = false;
    if (!res.ok) {
      addToast("error" in res ? res.error?.message || "Deposit failed" : "Deposit failed", "warning");
      return;
    }
    await refresh({ silent: true });
    return;
  }
  loading.value = true;
  const isInstance = !!(entry.instanceIds && entry.instanceIds.length);
  const res = isInstance
    ? entry.instanceIds!.length === 1
      ? await apiBankInstance(session.token, {
          characterId: session.character.id,
          instanceId: entry.instanceIds![0],
          direction: "deposit",
          clientRequestId: requestId()
        })
      : await apiBankInstances(session.token, {
          characterId: session.character.id,
          instanceIds: entry.instanceIds!,
          direction: "deposit",
          clientRequestId: requestId()
        })
    : await apiBankStackable(session.token, {
        characterId: session.character.id,
        itemId: entry.itemId,
        quantity: entry.quantity,
        direction: "deposit",
        clientRequestId: requestId()
      });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Deposit failed" : "Deposit failed", "warning");
    return;
  }
  await refresh({ silent: true });
}

async function withdrawEntry(entry: InventoryEntry) {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  const isStackable = !entry.instanceIds || entry.instanceIds.length === 0;
  if (isStackable) {
    const max = Math.max(1, Math.floor(Number(entry.quantity || 1)));
    const qty = await promptQuantity({
      title: "Withdraw quantity",
      subtitle: entry.item?.name || entry.itemId,
      icon: itemIconForType(entry.item?.type),
      min: 1,
      max,
      initial: max,
      confirmLabel: "Withdraw"
    });
    if (!qty) return;
    loading.value = true;
    const res = await apiBankStackable(session.token, {
      characterId: session.character.id,
      itemId: entry.itemId,
      quantity: qty,
      direction: "withdraw",
      clientRequestId: requestId()
    });
    loading.value = false;
    if (!res.ok) {
      addToast("error" in res ? res.error?.message || "Withdraw failed" : "Withdraw failed", "warning");
      return;
    }
    await refresh({ silent: true });
    return;
  }
  loading.value = true;
  const isInstance = !!(entry.instanceIds && entry.instanceIds.length);
  const res = isInstance
    ? entry.instanceIds!.length === 1
      ? await apiBankInstance(session.token, {
          characterId: session.character.id,
          instanceId: entry.instanceIds![0],
          direction: "withdraw",
          clientRequestId: requestId()
        })
      : await apiBankInstances(session.token, {
          characterId: session.character.id,
          instanceIds: entry.instanceIds!,
          direction: "withdraw",
          clientRequestId: requestId()
        })
    : await apiBankStackable(session.token, {
        characterId: session.character.id,
        itemId: entry.itemId,
        quantity: entry.quantity,
        direction: "withdraw",
        clientRequestId: requestId()
      });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Withdraw failed" : "Withdraw failed", "warning");
    return;
  }
  await refresh({ silent: true });
}

onMounted(async () => {
  await refreshContainers({ force: true });
});

watch(
  () => [props.available, tab.value] as const,
  async ([available, currentTab]) => {
    if (available && currentTab === "bank") {
      startBankStream();
      if (!state.value) {
        await refresh();
      }
      return;
    }
    stopBankStream();
  },
  { immediate: true }
);

onUnmounted(() => {
  stopBankStream();
});
</script>

<style scoped>
.bank-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.controls {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.bank-grid {
  display: grid;
  gap: 1rem;
}

.gold-kv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.kv-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  font-size: 1.1rem;
  font-weight: 800;
  margin-top: 0.25rem;
}

.search {
  display: inline-flex;
}

.ui-input {
  width: 160px;
  padding: 0.45rem 0.6rem;
}

.list {
  margin-top: 0.75rem;
  display: grid;
  gap: 0.6rem;
}

.item-row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
}

.item-name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.carry-grid {
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.containers-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
}

.container-row {
  width: 100%;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  text-align: left;
  color: inherit;
  cursor: pointer;
}

.container-row.active {
  border-color: color-mix(in srgb, var(--accent-econ) 48%, var(--glass-border-0));
  box-shadow: var(--shadow-accent);
}

.container-row__main {
  min-width: 0;
  display: grid;
  gap: 0.15rem;
}

.container-row__name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-weight: 850;
}

.container-item {
  width: 100%;
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  text-align: left;
  color: inherit;
  cursor: pointer;
}

.container-item__main {
  min-width: 0;
  display: grid;
  gap: 0.15rem;
}

.container-item__hint {
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

@media (min-width: 960px) {
  .containers-grid {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    align-items: start;
  }
}
</style>
